import { motion, useReducedMotion } from "framer-motion";

import { PRODUCTS } from "../data/products";
import { SmartLink } from "./SmartLink";

/**
 * The hero visual.
 *
 * The business requirement is that a visitor understands within ten
 * seconds that there are three ways to buy from us. Three cards would say
 * that, but so does every services site; what they cannot say is that the
 * three are the same line at different lengths. So the visual is three
 * lanes over one track — Consult stops at the decision, Build runs to
 * shipped, Product runs all the way to something still running today, with
 * the live products named at the end of it.
 *
 * The signal animation is the only continuously-moving thing on the page,
 * and it stops entirely under prefers-reduced-motion.
 */

const LANES = [
  {
    key: "consult",
    label: "Consult",
    span: 42,
    endpoint: "A decision you can act on",
    href: "/consulting",
    from: "rgba(16,185,129,0.9)",
    to: "rgba(20,184,166,0.9)",
  },
  {
    key: "build",
    label: "Build",
    span: 72,
    endpoint: "A system in production",
    href: "/software-development",
    from: "rgba(20,184,166,0.9)",
    to: "rgba(139,92,246,0.9)",
  },
  {
    key: "product",
    label: "Product",
    span: 100,
    endpoint: null,
    href: "/products",
    from: "rgba(139,92,246,0.9)",
    to: "rgba(217,70,239,0.9)",
  },
] as const;

export function HeroSystem() {
  const reduce = useReducedMotion();

  return (
    <div className="glass relative overflow-hidden p-5 sm:p-7">
      <div className="pointer-events-none absolute -right-16 -top-20 size-64 rounded-full bg-brand-violet/20 blur-[90px]" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 size-64 rounded-full bg-brand-emerald/20 blur-[90px]" />

      <div className="relative flex items-center justify-between gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">
          Three ways in
        </span>
        <span className="text-[10px] uppercase tracking-[0.16em] text-white/30">
          Think → Operate
        </span>
      </div>

      <div className="relative mt-5 space-y-4">
        {LANES.map((lane, i) => (
          <div key={lane.key}>
            <div className="mb-1.5 flex items-baseline justify-between gap-3">
              <SmartLink
                href={lane.href}
                className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/75 transition-colors hover:text-white"
              >
                {lane.label}
              </SmartLink>
              {lane.endpoint ? (
                <span className="truncate text-[11px] text-white/40">{lane.endpoint}</span>
              ) : null}
            </div>

            {/* Track */}
            <div className="relative h-[3px] w-full overflow-hidden rounded-full bg-white/[0.06]">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${lane.span}%` }}
                transition={{
                  duration: 0.9,
                  delay: 0.25 + i * 0.14,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${lane.from}, ${lane.to})`,
                }}
              />
              {/* Travelling signal. Purely decorative, so it is hidden from
               *  assistive tech and disabled under reduced motion. */}
              {!reduce ? (
                <motion.div
                  aria-hidden
                  initial={{ x: "-12%", opacity: 0 }}
                  animate={{ x: `${lane.span}%`, opacity: [0, 1, 1, 0] }}
                  transition={{
                    duration: 2.6,
                    delay: 1.1 + i * 0.35,
                    repeat: Infinity,
                    repeatDelay: 1.6,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-y-0 w-10 rounded-full bg-white/70 blur-[3px]"
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {/* The Product lane's endpoint is not a description — it is the
       *  products themselves. The live dot follows `status`: a pulsing
       *  emerald dot is this site's "in production" signal, and showing one
       *  next to a product nobody is using would be the claim the registry
       *  exists to stop. */}
      <div className="relative mt-4 flex flex-wrap items-center gap-2">
        {PRODUCTS.map((p, i) => (
          <motion.div
            key={p.slug}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.0 + i * 0.1 }}
          >
            <SmartLink
              href={p.href}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs text-white/80 transition-colors hover:border-white/25 hover:text-white"
            >
              {p.status === "live" ? (
                <span className="relative flex size-1.5">
                  {!reduce ? (
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                  ) : null}
                  <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
                </span>
              ) : (
                <span className="size-1.5 rounded-full border border-white/35" />
              )}
              {p.name}
            </SmartLink>
          </motion.div>
        ))}
        <span className="text-[11px] text-white/35">our own products</span>
      </div>
    </div>
  );
}
